import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import FormInput from '../components/FormInput'
import LoadingSpinner from '../components/LoadingSpinner'
import ConfirmDialog from '../components/ConfirmDialog'
import { toastUtil } from '../utils/toast'
import { isWeb, isIOS } from '../utils/platform'
import { authApi, userApi, ApiErrorResponse } from '../api'
import { SocialProvider } from '../constants/social'
import { handleGoogleLogin } from '../utils/googleAuth'
import { handleAppleLogin } from '../utils/appleAuth'
import { handleKakaoLogin as handleKakaoLoginUtil } from '../utils/kakaoAuth'
import { handleNaverLogin } from '../utils/naverAuth'
import { useAuthStore } from '../stores/authStore'
import {
  acquireBottomImmersive,
  releaseBottomImmersive,
} from '../utils/flutterStatusBar'

/**
 * 소셜 로그인 취소 메시지 여부.
 * 한글 '취소' 또는 영문 cancel 계열(user canceled request 등)이면 토스트를 띄우지 않는다.
 */
const isLoginCancel = (msg?: string): boolean => {
  if (!msg) return false
  return msg.includes('취소') || /cancel/i.test(msg)
}

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { setToken, setUserId, setProfileImage, getPostLoginPath } =
    useAuthStore()
  const [id, setId] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // FCM 토큰 등록은 App.tsx의 전역 핸들러(setupFcmBridge/registerFcm)가 전담한다.
  // (여기서 onFcmTokenReceived를 덮어쓰면 전역 핸들러가 파괴돼 토큰이 서버에 안 올라감)
  const [fcmToken] = useState<string | null>(null)

  // 로딩 오버레이가 떠 있는 동안 하단 홈인디케이터 스트립도 dim색으로 동기화한다
  // (센터 다이얼로그와 동일 처리 — 없으면 하단 스트립만 크림색으로 남아 dim이 어긋남)
  useEffect(() => {
    if (!isLoading) return
    acquireBottomImmersive()
    return () => releaseBottomImmersive()
  }, [isLoading])

  // FCM 토큰 저장 (비동기)
  const saveFcmTokenAsync = async (token: string) => {
    try {
      await userApi.saveFcmToken(token)
    } catch (error) {
      // FCM 토큰 저장 실패는 로그만 남기고 계속 진행
      console.error('FCM 토큰 저장 실패:', error)
    }
  }

  // 구글 로그인 성공 후 처리 함수
  const handleGoogleLoginSuccess = useCallback(
    async (providerId: string) => {
      try {
        const response = await authApi.socialLogin({
          provider: SocialProvider.GOOGLE,
          provider_id: providerId,
        })

        if (response.token) {
          setToken(response.token)
        }

        // 사용자 프로필 가져와서 ID 저장
        let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
        try {
          profileResponse = await userApi.getProfile()
          setUserId(profileResponse.user.id)
        } catch (error) {
          // 프로필 로드 실패 시 무시
        }

        // 로그인 성공 후 FCM 토큰 저장 (비동기)
        if (fcmToken) {
          saveFcmTokenAsync(fcmToken)
        }

        navigate(
          getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
        )
      } catch (error: any) {
        const apiError = error.response?.data as ApiErrorResponse
        const errorMessage =
          apiError?.message || '로그인 중 오류가 발생했습니다'

        // 탈퇴된 계정인 경우
        if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
          toastUtil.error('탈퇴한 계정입니다')
          return
        }

        if (errorMessage.includes('가입된 계정이 없습니다')) {
          const socialData = JSON.stringify({
            provider: SocialProvider.GOOGLE,
            provider_id: providerId,
          })
          const encodedData = btoa(socialData)
          // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
          navigate(`/register/phone?social=${encodedData}`)
        } else {
          toastUtil.error(errorMessage)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate, fcmToken, setToken, setUserId, getPostLoginPath]
  )

  // 카카오 로그인 성공 후 처리 함수
  const handleKakaoLoginSuccess = useCallback(
    async (providerId: string) => {
      console.log('[Kakao] handleKakaoLoginSuccess 호출, providerId:', providerId)
      try {
        console.log('[Kakao] authApi.socialLogin 시작')
        const response = await authApi.socialLogin({
          provider: SocialProvider.KAKAO,
          provider_id: providerId,
        })

        console.log('[Kakao] authApi.socialLogin 완료')
        if (response.token) {
          setToken(response.token)
        }

        // 사용자 프로필 가져와서 ID 저장
        let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
        try {
          profileResponse = await userApi.getProfile()
          setUserId(profileResponse.user.id)
        } catch (error) {
          // 프로필 로드 실패 시 무시
        }

        // 로그인 성공 후 FCM 토큰 저장 (비동기)
        if (fcmToken) {
          saveFcmTokenAsync(fcmToken)
        }

        navigate(
          getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
        )
      } catch (error: any) {
        console.log('[Kakao] handleKakaoLoginSuccess 에러:', error?.message || error)
        const apiError = error.response?.data as ApiErrorResponse
        const errorMessage =
          apiError?.message || '로그인 중 오류가 발생했습니다'

        // 탈퇴된 계정인 경우
        if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
          toastUtil.error('탈퇴한 계정입니다')
          return
        }

        if (errorMessage.includes('가입된 계정이 없습니다')) {
          const socialData = JSON.stringify({
            provider: SocialProvider.KAKAO,
            provider_id: providerId,
          })
          const encodedData = btoa(socialData)
          // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
          navigate(`/register/phone?social=${encodedData}`)
        } else {
          toastUtil.error(errorMessage)
        }
      } finally {
        console.log('[Kakao] handleKakaoLoginSuccess finally, setIsLoading(false)')
        setIsLoading(false)
      }
    },
    [navigate, fcmToken, setToken, setUserId, getPostLoginPath]
  )

  // 네이버 로그인 성공 후 처리 함수
  const handleNaverLoginSuccess = useCallback(
    async (providerId: string) => {
      try {
        const response = await authApi.socialLogin({
          provider: SocialProvider.NAVER,
          provider_id: providerId,
        })

        if (response.token) {
          setToken(response.token)
        }

        // 사용자 프로필 가져와서 ID 저장
        let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
        try {
          profileResponse = await userApi.getProfile()
          setUserId(profileResponse.user.id)
        } catch (error) {
          // 프로필 로드 실패 시 무시
        }

        // 로그인 성공 후 FCM 토큰 저장 (비동기)
        if (fcmToken) {
          saveFcmTokenAsync(fcmToken)
        }

        navigate(
          getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
        )
      } catch (error: any) {
        const apiError = error.response?.data as ApiErrorResponse
        const errorMessage =
          apiError?.message || '로그인 중 오류가 발생했습니다'

        // 탈퇴된 계정인 경우
        if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
          toastUtil.error('탈퇴한 계정입니다')
          return
        }

        if (errorMessage.includes('가입된 계정이 없습니다')) {
          const socialData = JSON.stringify({
            provider: SocialProvider.NAVER,
            provider_id: providerId,
          })
          const encodedData = btoa(socialData)
          // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
          navigate(`/register/phone?social=${encodedData}`)
        } else {
          toastUtil.error(errorMessage)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate, fcmToken, setToken, setUserId, getPostLoginPath]
  )

  // (FCM 토큰 수신 핸들러는 App.tsx 전역에서 상시 관리 — 여기서 등록/해제하지 않는다)

  // Flutter 구글 로그인 결과 콜백 등록
  useEffect(() => {
    const handleGoogleLoginResult = (data: {
      success: boolean
      error?: string
      providerId?: string
    }) => {
      if (data.success && data.providerId) {
        handleGoogleLoginSuccess(data.providerId)
      } else {
        const errorMsg = data.error || '구글 로그인 중 오류가 발생했습니다'
        if (!isLoginCancel(errorMsg)) {
          toastUtil.error(errorMsg)
        }
        setIsLoading(false)
      }
    }

    // 이벤트 리스너 방식 (콜백 등록 전에도 동작)
    const handleEvent = (event: CustomEvent) => {
      handleGoogleLoginResult(event.detail)
    }

    if (!isWeb()) {
      // CustomEvent 리스너 등록
      window.addEventListener(
        'flutterGoogleLoginResult',
        handleEvent as EventListener
      )

      // 기존 콜백 방식도 지원 (하위 호환성)
      if ((window as any).FlutterBridge) {
        ;(window as any).FlutterBridge.onGoogleLoginResult =
          handleGoogleLoginResult
      }
    }

    return () => {
      if (!isWeb()) {
        window.removeEventListener(
          'flutterGoogleLoginResult',
          handleEvent as EventListener
        )
        if ((window as any).FlutterBridge) {
          ;(window as any).FlutterBridge.onGoogleLoginResult = undefined
        }
      }
    }
  }, [handleGoogleLoginSuccess])

  // Flutter 카카오 로그인 결과 (CustomEvent + 콜백 + 폴링)
  const handleKakaoLoginResultRef = useRef<(data: {
    success: boolean
    error?: string
    providerId?: string
  }) => void | null>(null)

  useEffect(() => {
    const handleKakaoLoginResult = (data: {
      success: boolean
      error?: string
      providerId?: string
    }) => {
      console.log('[Kakao] handleKakaoLoginResult 수신:', data)
      delete (window as any).__kakaoLoginResult
      if (data.success && data.providerId) {
        handleKakaoLoginSuccess(data.providerId)
      } else {
        const errorMsg = data.error || '카카오 로그인 중 오류가 발생했습니다'
        if (!isLoginCancel(errorMsg)) {
          toastUtil.error(errorMsg)
        }
        setIsLoading(false)
      }
    }

    handleKakaoLoginResultRef.current = handleKakaoLoginResult

    const handleEvent = (event: CustomEvent) => {
      console.log('[Kakao] flutterKakaoLoginResult CustomEvent 수신')
      handleKakaoLoginResult(event.detail)
    }

    if (!isWeb()) {
      console.log('[Kakao] 이벤트 리스너 등록')
      window.addEventListener(
        'flutterKakaoLoginResult',
        handleEvent as EventListener
      )
      if ((window as any).FlutterBridge) {
        ;(window as any).FlutterBridge.onKakaoLoginResult =
          handleKakaoLoginResult
      }
    }

    return () => {
      handleKakaoLoginResultRef.current = null
      if (!isWeb()) {
        window.removeEventListener(
          'flutterKakaoLoginResult',
          handleEvent as EventListener
        )
        if ((window as any).FlutterBridge) {
          ;(window as any).FlutterBridge.onKakaoLoginResult = undefined
        }
      }
    }
  }, [handleKakaoLoginSuccess])

  // Custom Tab 복귀 시 이벤트 누락 대비 폴링 (window.__kakaoLoginResult)
  useEffect(() => {
    if (isWeb() || !isLoading) return

    const interval = setInterval(() => {
      const data = (window as any).__kakaoLoginResult
      if (data && handleKakaoLoginResultRef.current) {
        console.log('[Kakao] 폴링으로 결과 수신:', data)
        handleKakaoLoginResultRef.current(data)
      }
    }, 300)

    return () => clearInterval(interval)
  }, [isLoading])

  // Flutter 네이버 로그인 결과 (구글/카카오와 동일: CustomEvent + 콜백)
  useEffect(() => {
    const handleNaverLoginResult = (data: {
      success: boolean
      error?: string
      providerId?: string
    }) => {
      if (data.success && data.providerId) {
        handleNaverLoginSuccess(data.providerId)
      } else {
        const errorMsg = data.error || '네이버 로그인 중 오류가 발생했습니다'
        if (!isLoginCancel(errorMsg)) {
          toastUtil.error(errorMsg)
        }
        setIsLoading(false)
      }
    }

    const handleEvent = (event: CustomEvent) => {
      handleNaverLoginResult(event.detail)
    }

    if (!isWeb()) {
      window.addEventListener(
        'flutterNaverLoginResult',
        handleEvent as EventListener
      )
      if ((window as any).FlutterBridge) {
        ;(window as any).FlutterBridge.onNaverLoginResult =
          handleNaverLoginResult
      }
    }

    return () => {
      if (!isWeb()) {
        window.removeEventListener(
          'flutterNaverLoginResult',
          handleEvent as EventListener
        )
        if ((window as any).FlutterBridge) {
          ;(window as any).FlutterBridge.onNaverLoginResult = undefined
        }
      }
    }
  }, [handleNaverLoginSuccess])

  // 애플 로그인 성공 후 처리 함수
  const handleAppleLoginSuccess = useCallback(
    async (providerId: string) => {
      try {
        const response = await authApi.socialLogin({
          provider: SocialProvider.APPLE,
          provider_id: providerId,
        })

        if (response.token) {
          setToken(response.token)
        }

        // 사용자 프로필 가져와서 ID 저장
        let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
        try {
          profileResponse = await userApi.getProfile()
          setUserId(profileResponse.user.id)
        } catch (error) {
          // 프로필 로드 실패 시 무시
        }

        // 로그인 성공 후 FCM 토큰 저장 (비동기)
        if (fcmToken) {
          saveFcmTokenAsync(fcmToken)
        }

        navigate(
          getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
        )
      } catch (error: any) {
        const apiError = error.response?.data as ApiErrorResponse
        const errorMessage =
          apiError?.message || '로그인 중 오류가 발생했습니다'

        // 탈퇴된 계정인 경우
        if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
          toastUtil.error('탈퇴한 계정입니다')
          return
        }

        if (errorMessage.includes('가입된 계정이 없습니다')) {
          const socialData = JSON.stringify({
            provider: SocialProvider.APPLE,
            provider_id: providerId,
          })
          const encodedData = btoa(socialData)
          // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
          navigate(`/register/phone?social=${encodedData}`)
        } else {
          toastUtil.error(errorMessage)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate, fcmToken, setToken, setUserId, getPostLoginPath]
  )

  // Flutter 애플 로그인 결과 콜백 등록
  useEffect(() => {
    const handleAppleLoginResult = (data: {
      success: boolean
      error?: string
      providerId?: string
    }) => {
      if (data.success && data.providerId) {
        handleAppleLoginSuccess(data.providerId)
      } else {
        const errorMsg = data.error || '애플 로그인 중 오류가 발생했습니다'
        if (!isLoginCancel(errorMsg)) {
          toastUtil.error(errorMsg)
        }
        setIsLoading(false)
      }
    }

    // 이벤트 리스너 방식으로 전송 (콜백 등록 전에도 동작)
    const handleEvent = (event: CustomEvent) => {
      handleAppleLoginResult(event.detail)
    }

    if (!isWeb()) {
      // CustomEvent 리스너 등록
      window.addEventListener(
        'flutterAppleLoginResult',
        handleEvent as EventListener
      )

      // 기존 콜백 방식도 지원 (하위 호환성)
      if ((window as any).FlutterBridge) {
        ;(window as any).FlutterBridge.onAppleLoginResult =
          handleAppleLoginResult
      }
    }

    return () => {
      if (!isWeb()) {
        window.removeEventListener(
          'flutterAppleLoginResult',
          handleEvent as EventListener
        )
        if ((window as any).FlutterBridge) {
          ;(window as any).FlutterBridge.onAppleLoginResult = undefined
        }
      }
    }
  }, [handleAppleLoginSuccess])

  const handleGoogleLoginClick = async () => {
    if (isLoading) {
      toastUtil.error('처리 중입니다')
      return
    }

    setIsLoading(true)

    // 앱 환경에서는 Flutter로 구글 로그인 요청 (iOS 웹뷰에서 팝업 차단 방지)
    if (!isWeb() && (window as any).FlutterBridge?.requestGoogleLogin) {
      ;(window as any).FlutterBridge.requestGoogleLogin()
      return
    }

    // 웹 환경에서는 Firebase Auth 팝업 사용
    await handleGoogleLogin(handleGoogleLoginSuccess, (error: string) => {
      if (!isLoginCancel(error)) {
        toastUtil.error(error)
      }
      setIsLoading(false)
    })
  }

  const handleKakaoLogin = async () => {
    if (isLoading) {
      toastUtil.error('처리 중입니다')
      return
    }

    setIsLoading(true)

    // 앱 환경에서는 Flutter로 카카오 로그인 요청
    if (!isWeb() && (window as any).FlutterBridge?.requestKakaoLogin) {
      ;(window as any).FlutterBridge.requestKakaoLogin()
      return
    }

    // 웹 환경에서는 기존 방식 사용
    await handleKakaoLoginUtil(
      async (providerId: string) => {
        try {
          const response = await authApi.socialLogin({
            provider: SocialProvider.KAKAO,
            provider_id: providerId,
          })

          if (response.token) {
            setToken(response.token)
          }

          // 사용자 프로필 가져와서 ID 저장
          let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
          try {
            profileResponse = await userApi.getProfile()
            setUserId(profileResponse.user.id)
          } catch (error) {
            // 프로필 로드 실패 시 무시
          }

          // 로그인 성공 후 FCM 토큰 저장 (비동기)
          if (fcmToken) {
            saveFcmTokenAsync(fcmToken)
          }

          navigate(
            getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
          )
        } catch (error: any) {
          const apiError = error.response?.data as ApiErrorResponse
          const errorMessage =
            apiError?.message || '로그인 중 오류가 발생했습니다'

          // 탈퇴된 계정인 경우
          if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
            toastUtil.error('탈퇴한 계정입니다')
            return
          }

          if (errorMessage.includes('가입된 계정이 없습니다')) {
            const socialData = JSON.stringify({
              provider: SocialProvider.KAKAO,
              provider_id: providerId,
            })
            const encodedData = btoa(socialData)
            // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
            navigate(`/register/phone?social=${encodedData}`)
          } else {
            toastUtil.error(errorMessage)
          }
        } finally {
          setIsLoading(false)
        }
      },
      (error: string) => {
        if (!isLoginCancel(error)) {
          toastUtil.error(error)
        }
        setIsLoading(false)
      }
    )
  }

  const handleNaverLoginClick = () => {
    if (isLoading) {
      toastUtil.error('처리 중입니다')
      return
    }

    setIsLoading(true)

    // 앱 환경에서는 Flutter로 네이버 로그인 요청
    if (!isWeb() && (window as any).FlutterBridge?.requestNaverLogin) {
      ;(window as any).FlutterBridge.requestNaverLogin()
      return
    }

    // 웹 환경에서는 기존 방식 사용
    handleNaverLogin()
  }

  const handleAppleLoginClick = async () => {
    if (isLoading) {
      toastUtil.error('처리 중입니다')
      return
    }

    setIsLoading(true)

    // iOS 앱 웹뷰에서는 Flutter가 네이티브 애플 로그인 처리
    if (
      !isWeb() &&
      isIOS() &&
      (window as any).FlutterBridge?.requestAppleLogin
    ) {
      ;(window as any).FlutterBridge.requestAppleLogin()
      return
    }

    // 웹 브라우저에서는 Firebase OAuth 팝업으로 애플 로그인
    if (isWeb()) {
      await handleAppleLogin(handleAppleLoginSuccess, (error: string) => {
        if (!isLoginCancel(error)) {
          toastUtil.error(error)
        }
        setIsLoading(false)
      })
      return
    }

    toastUtil.error('애플 로그인은 iOS에서만 사용 가능합니다')
    setIsLoading(false)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!id || !password) {
      toastUtil.error('아이디와 비밀번호를 입력해주세요')
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.login({
        username: id,
        password,
      })

      if (response.token) {
        setToken(response.token)
      }

      // 사용자 프로필 가져와서 ID 및 프로필 이미지 저장
      let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
      try {
        profileResponse = await userApi.getProfile()
        setUserId(profileResponse.user.id)
        setProfileImage(profileResponse.user.profile_image || null)
      } catch (error) {
        // 프로필 로드 실패 시 무시
      }

      // 로그인 성공 후 FCM 토큰 저장 (비동기)
      if (fcmToken) {
        saveFcmTokenAsync(fcmToken)
      }

      navigate(
        getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
      )
    } catch (error: any) {
      const apiError = error.response?.data as ApiErrorResponse

      // 탈퇴된 계정인 경우.
      // 복구는 제공하지 않는다(정책). 예전에는 여기서 활성화 모달을 띄웠는데,
      // 그 복구 API가 아이디만 받고 인증이 없어 남의 계정도 되살릴 수 있었다.
      if (apiError?.errors?.is_deleted || apiError?.is_deleted) {
        toastUtil.error('탈퇴한 계정입니다')
        setIsLoading(false)
        return
      }

      if (apiError?.errors?.username) {
        toastUtil.error(apiError.errors.username[0])
      } else {
        toastUtil.error(apiError?.message || '로그인 중 오류가 발생했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderSocialButton = (opts: {
    bg: string
    icon: string
    label: string
    textColor: string
    border?: boolean
    iconSize?: number
    onClick: () => void
  }) => (
    <button
      type="button"
      onClick={opts.onClick}
      className={`relative w-full h-56px rounded-[999px] flex items-center justify-center active:opacity-90 ${
        opts.border ? 'border border-472410-10%' : ''
      }`}
      style={{ backgroundColor: opts.bg }}
    >
      <img
        src={opts.icon}
        alt=""
        className="absolute object-contain"
        style={{
          left: 40,
          width: opts.iconSize ?? 40,
          height: opts.iconSize ?? 40,
        }}
      />
      <span
        className="font-pretendard font-700 text-16px"
        style={{ color: opts.textColor }}
      >
        {opts.label}
      </span>
    </button>
  )

  return (
    <div className="w-full h-full flex justify-center items-center bg-backgroundColorBg2">
      {/* 진입 시 콘텐츠가 부드럽게 페이드+상승 (Welcome→로그인 하드컷 제거) */}
      <style>{`@keyframes login-appear{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div
        className="app-container px-20px h-full flex flex-col justify-center"
        style={{ animation: 'login-appear 0.45s cubic-bezier(.2,.8,.2,1) both' }}
      >
        {/* 소셜 로그인 버튼 (TASTEE 워드마크 제거 — 소셜+휴대폰 섹션이 화면 중앙에 오도록) */}
        <div className="flex flex-col gap-16px">
          {renderSocialButton({
            bg: '#FEE500',
            icon: '/images/kakao-symbol.png?v=2',
            label: '카카오로 계속하기',
            textColor: '#3C1E1E',
            iconSize: 20,
            onClick: handleKakaoLogin,
          })}
          {renderSocialButton({
            bg: '#03C75A',
            icon: '/images/naver-symbol.png?v=2',
            label: '네이버로 계속하기',
            textColor: '#FFFFFF',
            iconSize: 20,
            onClick: handleNaverLoginClick,
          })}
          {(isWeb() || isIOS()) &&
            renderSocialButton({
              bg: '#121417',
              icon: '/images/apple-symbol.png?v=2',
              label: '애플로 계속하기',
              textColor: '#FFFFFF',
              iconSize: 20,
              onClick: handleAppleLoginClick,
            })}
          {renderSocialButton({
            bg: '#FFFFFF',
            icon: '/images/google-symbol.png?v=2',
            label: '구글로 계속하기',
            textColor: '#222222',
            border: true,
            iconSize: 20,
            onClick: handleGoogleLoginClick,
          })}
        </div>

        {/* 또는 */}
        <div className="flex items-center gap-10px my-24px">
          <div className="flex-1 h-1px bg-divider-divider-1"></div>
          <span className="font-pretendard font-400 text-14px text-textColorText3">
            또는
          </span>
          <div className="flex-1 h-1px bg-divider-divider-1"></div>
        </div>

        {/* 휴대폰번호로 계속하기 */}
        <button
          type="button"
          onClick={() => navigate('/register/phone')}
          className="w-full py-16px rounded-[999px] bg-4B3621-80% flex items-center justify-center active:opacity-90"
        >
          <span className="font-pretendard font-700 text-16px text-textColorText1W">
            휴대폰번호로 계속하기
          </span>
        </button>
      </div>

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[1050] flex items-center justify-center">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      )}

    </div>
  )
}

export default Login
